export const REM = 16

export const MIN_WIDTH = 3

export type ColumnLayout<S extends string> = {
  id: string
  widths: Record<S, number>
  fill: S
  pinned?: readonly S[]
}

export type ColumnSizing<S extends string> = ColumnLayout<S> & {
  extras: readonly S[]
  dropOrder: readonly S[]
}

export type ColumnWidths<S extends string> = Partial<Record<S, number>>

export function gridTemplate<S extends string>(
  slots: readonly S[],
  layout: ColumnLayout<S>,
  resized: ColumnWidths<S> = {},
): string {
  const fill = slots.includes(layout.fill) ? layout.fill : slots.at(-1)
  const tracks = slots.map((slot) => {
    const width = resized[slot] ?? layout.widths[slot]
    if (slot !== fill) return `${width}rem`
    const max = resized[slot] === undefined ? "1fr" : `${width}rem`
    return `minmax(var(--column-min,${width}rem),${max})`
  })
  const filler = fill !== undefined && resized[fill] !== undefined
  return [...tracks, ...(filler ? ["1fr"] : [])].join(" ")
}

export function withWidths<S extends string>(
  sizing: ColumnSizing<S>,
  resized: ColumnWidths<S>,
): ColumnSizing<S> {
  return { ...sizing, widths: { ...sizing.widths, ...resized } }
}

export function fitColumns<S extends string, C extends S>(
  columns: C[],
  width: number | undefined,
  sizing: ColumnSizing<S>,
): { shown: C[]; hidden: C[] } {
  if (!width) return { shown: columns, hidden: [] }
  const extras = sizing.extras.reduce(
    (sum, slot) => sum + sizing.widths[slot],
    0,
  )
  const need = (candidates: C[]) =>
    candidates.reduce((sum, column) => sum + sizing.widths[column], extras) *
    REM
  let shown = columns
  const hidden: C[] = []
  for (const slot of sizing.dropOrder) {
    if (need(shown) <= width) break
    const column = shown.find((candidate) => candidate === slot)
    if (!column) continue
    shown = shown.filter((candidate) => candidate !== column)
    hidden.push(column)
  }
  return { shown, hidden }
}
